import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Officer-addressed cluster state.
 *
 * UI-only slice — purposely separate from `aiAnalysisSlice` so it is NOT
 * persisted to Supabase via `saveCallData`. This represents transient layout
 * state: when the officer addresses a question, related actions (top-K
 * reranked siblings, captured at action-create time in `dedupCandidates`) are
 * grouped under it as indented children.
 *
 * Multi-cluster lifecycle (2026-05-04):
 *   no clusters + addressed(A)
 *     → push{root:A, focus:A, children:topK(A), topic:topic(A)}
 *   active{root:A,...} + addressed(A or any child of A)
 *     → engage(A) (refresh lastInteraction, bump focus)
 *   active{root:A,...} + addressed(B in cluster X)
 *     → engage(X)
 *   active{root:A,...} + addressed(Z, no cluster)
 *     → release any cluster whose root is done/complete
 *     → push new cluster around Z; older clusters remain unless reaped
 *   any cluster older than MAX_CLUSTER_DWELL_MS
 *     → release that cluster
 *   any cluster where all members resolved/discarded
 *     → release that cluster (handled by reaper)
 *
 * New actions arrive (in transcript monitor):
 *   compute join-score against every active cluster's members; if best
 *   score >= MIN_SIMILARITY, joinCluster on that cluster.
 */

export interface ActiveCluster {
  rootId: string;
  /** Most recently officer-addressed member (root or any child). Drives visual emphasis. */
  focusId: string;
  /** Ordered child IDs, sorted by similarity to root descending. */
  childIds: string[];
  /**
   * Stable signature used to route new questions to this cluster as the
   * conversation progresses. Populated at form time from the root's
   * topic-bearing tokens (TOPIC_TOKENS hits + non-stopword content tokens).
   * Matched against new actions in addition to dedupCandidates.
   */
  topicTokens: string[];
  /** Entity IDs the cluster centers on (sourceEntityId + entityIds union). */
  topicEntityIds: string[];
  /** Short human-readable label, e.g. "robbery / weapon / suspect". For debug + tooltip. */
  topicLabel: string;
  /** Epoch ms — when this cluster originally formed (used for max-dwell cap). */
  addressedAt: number;
  /** Epoch ms — last time the officer addressed root or any child (used for engagement). */
  lastInteractionAt: number;
}

export interface OfficerClusterState {
  /** All clusters that are currently alive. Order = formation order, oldest first. */
  activeClusters: ActiveCluster[];
  /**
   * Set of action IDs that should NOT be auto-archived even when answered,
   * because they are part of an active cluster. The cluster reaper flushes
   * these to the answered carousel only on cluster release.
   */
  deferredArchiveIds: string[];
}

const initialState: OfficerClusterState = {
  activeClusters: [],
  deferredArchiveIds: [],
};

interface FormClusterPayload {
  rootId: string;
  childIds: string[];
  topicTokens: string[];
  topicEntityIds: string[];
  topicLabel: string;
  addressedAt: number;
}

const officerClusterSlice = createSlice({
  name: 'officerCluster',
  initialState,
  reducers: {
    /**
     * Form a new cluster around an action and append it to the active list.
     * Caller has already verified there's no existing cluster containing this
     * action. `childIds` MUST be pre-filtered (visible-only, same-type,
     * score >= MIN_SIMILARITY) and pre-sorted by similarity desc.
     */
    formCluster: (state, action: PayloadAction<FormClusterPayload>) => {
      const { rootId, childIds, topicTokens, topicEntityIds, topicLabel, addressedAt } = action.payload;
      // Defensive: skip if a cluster already exists with this root id.
      if (state.activeClusters.some((c) => c.rootId === rootId)) {
        return;
      }
      state.activeClusters.push({
        rootId,
        focusId: rootId,
        childIds,
        topicTokens,
        topicEntityIds,
        topicLabel,
        addressedAt,
        lastInteractionAt: addressedAt,
      });
    },

    /**
     * Officer addressed a member of an active cluster. Refresh
     * lastInteractionAt and bump focus to the addressed member so the UI can
     * highlight it. Does NOT mutate childIds.
     */
    engageCluster: (
      state,
      action: PayloadAction<{ memberId: string; at: number }>,
    ) => {
      const { memberId, at } = action.payload;
      for (const cluster of state.activeClusters) {
        const inThis =
          cluster.rootId === memberId || cluster.childIds.includes(memberId);
        if (!inThis) continue;
        if (at < cluster.lastInteractionAt) return;
        cluster.lastInteractionAt = at;
        cluster.focusId = memberId;
        return;
      }
    },

    /**
     * Append a newly-arrived action to a specific cluster's children. Caller
     * verifies type match and that the action isn't already in the cluster
     * and that the score is above MIN_SIMILARITY. `clusterRootId` selects
     * which cluster to join (since multiple may be active).
     */
    joinCluster: (
      state,
      action: PayloadAction<{ clusterRootId: string; memberId: string; score: number }>,
    ) => {
      const { clusterRootId, memberId } = action.payload;
      const cluster = state.activeClusters.find((c) => c.rootId === clusterRootId);
      if (!cluster) return;
      if (cluster.rootId === memberId) return;
      if (cluster.childIds.includes(memberId)) return;
      cluster.childIds.push(memberId);
      // Cap children at CLUSTER_TOP_K — drop weakest if over.
      if (cluster.childIds.length > CLUSTER_TOP_K) {
        cluster.childIds = cluster.childIds.slice(0, CLUSTER_TOP_K);
      }
    },

    /**
     * Mark an action ID as deferred-archive (don't run countdown while clustered).
     * Idempotent.
     */
    addDeferredArchive: (state, action: PayloadAction<string>) => {
      if (!state.deferredArchiveIds.includes(action.payload)) {
        state.deferredArchiveIds.push(action.payload);
      }
    },

    /**
     * Release a specific cluster by its rootId. Children flow back to their
     * normal grouping automatically (membership is computed at render time).
     * If `rootId` is omitted, release ALL active clusters (legacy behavior).
     */
    releaseCluster: (state, action: PayloadAction<{ rootId?: string } | undefined>) => {
      const targetRoot = action.payload?.rootId;
      if (!targetRoot) {
        state.activeClusters = [];
        state.deferredArchiveIds = [];
        return;
      }
      const idx = state.activeClusters.findIndex((c) => c.rootId === targetRoot);
      if (idx === -1) return;
      const removed = state.activeClusters.splice(idx, 1)[0];
      // Drop deferred-archive entries that belonged ONLY to the removed cluster.
      if (removed) {
        const removedMembers = new Set([removed.rootId, ...removed.childIds]);
        const stillNeeded = new Set<string>();
        for (const c of state.activeClusters) {
          stillNeeded.add(c.rootId);
          for (const id of c.childIds) stillNeeded.add(id);
        }
        state.deferredArchiveIds = state.deferredArchiveIds.filter(
          (id) => !removedMembers.has(id) || stillNeeded.has(id),
        );
      }
    },
  },
});

export const {
  formCluster,
  engageCluster,
  joinCluster,
  addDeferredArchive,
  releaseCluster,
} = officerClusterSlice.actions;

export default officerClusterSlice.reducer;

// ── Constants ────────────────────────────────────────────────────────────────
export const CLUSTER_TOP_K = 5;
export const CLUSTER_MIN_SIMILARITY = 0.60;
/**
 * Per-cluster max dwell. Each cluster's reaper trips independently.
 *
 * This is the ceiling on how long an ANSWERED card can stay on screen: while a
 * cluster is alive its members do not auto-archive (cluster-deferred archive),
 * so the card waits for release — the officer addressing an unrelated action,
 * all members going away, or this cap.
 *
 * Was 5 minutes. Owner call 2026-08-10: too long. Completed cards reading as
 * "stuck" was TWO problems — a missing effect dependency meant they never
 * cleared even after release (fixed separately, see clusterMembershipSignature),
 * and this cap made the legitimate hold itself feel indefinite. 30s default.
 *
 * Env-tunable so it can be retuned without a rebuild, same pattern and same
 * validation shape as NEXT_PUBLIC_ACTION_REMOVAL_COUNTDOWN_MS. Override via
 * NEXT_PUBLIC_MAX_CLUSTER_DWELL_MS (non-negative integer ms); unset, blank, or
 * invalid falls back to the default. Read at module load, like its sibling —
 * NEXT_PUBLIC_* is inlined at build time, so a value must be present when the
 * image is built, not just at runtime.
 */
export const DEFAULT_MAX_CLUSTER_DWELL_MS = 30 * 1000;

function resolveMaxClusterDwellMs(): number {
  const raw = process.env.NEXT_PUBLIC_MAX_CLUSTER_DWELL_MS;
  if (raw === undefined || raw.trim() === '') {
    return DEFAULT_MAX_CLUSTER_DWELL_MS;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return DEFAULT_MAX_CLUSTER_DWELL_MS;
  }
  return parsed;
}

export const MAX_CLUSTER_DWELL_MS = resolveMaxClusterDwellMs();

/** Feature flag — flip to disable cluster behavior entirely (flat list). */
export const ENABLE_OFFICER_ADDRESSED_CLUSTER = true;
